import React, { useId } from "react";

interface SparklineProps {
  /** Chronological values; the last point is the current period. */
  values: number[];
  width?: number;
  height?: number;
  className?: string;
}

/**
 * Minimal 12-point sparkline: the history draws in a de-emphasis gray, the
 * current period gets the accent end-dot. Pure SVG, no dependencies.
 */
export const Sparkline: React.FC<SparklineProps> = ({
  values,
  width = 96,
  height = 28,
  className = "",
}) => {
  const id = useId();
  const points = values.slice(-12);
  if (points.length < 2) return null;

  const pad = 3;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;

  const x = (i: number) => pad + (i / (points.length - 1)) * (width - pad * 2);
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2);

  const path = points.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const lastX = x(points.length - 1);
  const lastY = y(points[points.length - 1]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`spark-fill-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.12" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={`${path} L${lastX.toFixed(1)},${height - pad} L${x(0).toFixed(1)},${height - pad} Z`}
        fill={`url(#spark-fill-${id})`}
        className="text-primary"
      />
      <path
        d={path}
        fill="none"
        stroke="rgba(255,255,255,0.28)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Accent end-dot with a surface ring so it reads over the line */}
      <circle cx={lastX} cy={lastY} r="3.5" className="fill-background" />
      <circle cx={lastX} cy={lastY} r="2.5" className="fill-primary" />
    </svg>
  );
};

export default Sparkline;
